<template>
    <Transition name="fade" mode="out-in">
        <div v-if="loading" class="flex flex-col">
            <MiscSkeleton color="bg-base-100" size="large" :repeat="2" />
            <MiscSkeleton 
                color="bg-base-100" 
                text 
                size="small" 
                :repeat="6" 
            />
            <MiscSkeleton color="bg-base-100" size="large" :repeat="3" />
        </div>
        <div v-else-if="!loading" class="flex flex-col">
            <div class="lg:overflow-y-auto lg:border lg:rounded bg-base-100" :class="showFullCartList ? 'lg:max-h-full' : 'lg:max-h-80'">
                <CartList :is-interactive="isInteractive" class="!pb-0" />
                <div
                    v-if="cart?.lineItems && cart?.lineItems?.filter((item) => !isCoupon(item.type)).length > cartDisplayLimit"
                    class="flex justify-center sticky bottom-0 bg-base-100 z-[2]"
                >
                    <button
                        class="hidden lg:inline-flex lg:w-full items-center btn btn-icon-right btn-ghost"
                        @click="toggleCartList()"
                    >
                        <template v-if="!showFullCartList">
                            <span>{{ $t('cart.list.show') }}</span>
                            <MiscIcon name="chevron-down" />
                        </template>
                        <template v-else>
                            <span>{{ $t('cart.list.hide') }}</span>
                            <MiscIcon name="chevron-up" />
                        </template>
                    </button>
                </div>
            </div>

            <div class="flex justify-between">
                <div v-if="cart?.lineItems && cart?.lineItems?.length > 0" class="w-full py-6">
                    <CartCoupons
                        :coupons="getCoupons"
                        :is-interactive="isInteractive"
                        class="bg-base-200"
                        :show-col="true"
                    />
                </div>
            </div>

            <div class="lg:border lg:rounded bg-base-100">
                <CartTotals :is-interactive="isInteractive" :show-savings="false" class="!border-base-300 !px-0" />
            </div>
            <div v-if="!isMobile && !isInteractive" class="flex p-4 lg:p-0 lg:pt-2 gap-1 items-center text-base-content text-xs font-normal">
                <MiscIcon name="lock" size="xs" />
                {{ $t('drawer.contextCart.transaction') }}
                <div class="font-bold underline lowercase">
                    {{ $t('drawer.contextCart.safe') }}
                </div>
            </div>

            <div v-if="cart?.lineItems && cart?.lineItems.length > 0 && isInteractive" class="flex flex-col w-full gap-6">
                <MiscLink
                    :to="checkoutEntryPath"
                    class="btn btn-accent text-primary h-14"
                    data-cy="checkoutLink"
                >
                    {{ $t('cart.actions.checkout') }}
                </MiscLink>

                <MiscLink no-prefetch to="/cart" class="btn btn-outline">
                    {{ $t('cart.actions.editCart') }}
                </MiscLink>
            </div>
        </div>
    </Transition>
</template>

<script setup lang="ts">
import type { Schemas } from '#shopware'
import { useWindowSize } from '@vueuse/core'

withDefaults(defineProps<{
    isInteractive?: boolean
}>(), {
    isInteractive: true
})

const { cart, refreshCart } = useCart()
const { width: windowWidth } = useWindowSize()
const nuxtApp = useNuxtApp()
const { checkoutEntryPath } = useCheckoutEntry()

const isMobile = computed(() => {
    return windowWidth.value <= 375
})

const loading = ref(true)

onMounted(async() => {
    await refreshCart()
    if (cart.value) nuxtApp.callHook('viewCart', { cart: cart.value })
    loading.value = false
})

const cartDisplayLimit = ref(3)
const showFullCartList = ref(cart?.value?.lineItems != undefined && cart?.value?.lineItems.filter((item) => !isCoupon(item.type)).length < cartDisplayLimit.value)

function toggleCartList () {
    showFullCartList.value = !showFullCartList.value
}

function isCoupon (type: string | undefined) {
    return type === 'promotion' || type === 'easy-coupon'
}

function isFreeGift (lineItem: Schemas['LineItem']) {
    const payloadType = (lineItem.payload as Record<string, unknown> | undefined)?.line_item_type
    return payloadType === "promotion-gift-wrapper"
}

const getCoupons = computed(() => {
    return cart?.value?.lineItems?.filter((item) => isCoupon(item.type) && !isFreeGift(item))
})
</script>
